import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { Organization, OrganizationType } from '@cast/types';

import { useNavigate } from './useNavigate';

import { ORGANIZATION_ROUTE_CONFIG } from './navigation';

type UseOrganizationRouteValidationParams = {
  currentOrganization: Organization | null;
};

/**
 * Hook that validates organization routes and redirects users to the correct
 * route based on their current organization's type.
 *
 * - ENTERPRISE orgs → /organization/enterprise/:id/*
 * - CHILD orgs → /organization/enterprise/:parentId/* (with ?org=childId) OR
 *                /organization/management/* (with ?org=childId) - backwards compatibility
 * - DEFAULT orgs → /organization/management/*
 */
export const useOrganizationRouteValidation = ({
  currentOrganization,
}: UseOrganizationRouteValidationParams) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      !currentOrganization ||
      !location.pathname.startsWith('/organization')
    ) {
      return;
    }

    const isOnEnterpriseRoute = location.pathname.includes(
      '/organization/enterprise'
    );
    const isOnManagementRoute = location.pathname.includes(
      '/organization/management'
    );

    // Skip redirect for other organization routes
    if (!isOnEnterpriseRoute && !isOnManagementRoute) {
      return;
    }

    // Get the target route for this organization type
    const routeBuilder = ORGANIZATION_ROUTE_CONFIG[currentOrganization.type];
    const targetRoute = routeBuilder ? routeBuilder(currentOrganization) : null;

    if (!targetRoute) {
      console.error('Unknown organization type:', currentOrganization.type);
      return;
    }

    // Check if we're already on the correct route type
    const isOnCorrectRoute =
      (currentOrganization.type === OrganizationType.ENTERPRISE &&
        isOnEnterpriseRoute) ||
      (currentOrganization.type === OrganizationType.CHILD &&
        // Allow both routes for backwards compatibility.
        // TODO(UI-5984): isOnManagementRoute check can be removed after classic UI is removed
        (isOnEnterpriseRoute || isOnManagementRoute)) ||
      (currentOrganization.type === OrganizationType.DEFAULT &&
        isOnManagementRoute);

    // Redirect if on wrong route type
    if (!isOnCorrectRoute) {
      navigate(targetRoute, { replace: true });
    }
  }, [currentOrganization, location.pathname, navigate]);
};
